import {
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  Select,
  Switch,
  Typography
} from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type { ApplicationRepository } from 'src/api/main'
import { paths } from 'src/router'

import { useWorkspaceAppsList } from '../../hooks/useWorkspaceAppsList'
import {
  createDefaultAppProductConfig,
  saveAppProductConfig,
  type AppProductConfig
} from '../../utils/appProductConfig'

const { Text } = Typography

type Props = {
  workspaceId: number
  repo: ApplicationRepository | null
  open: boolean
  onClose: () => void
  onInstalled?: (appId: string) => void
}

type FormValues = {
  name: string
  description?: string
  /** Hand the app to every workspace member with `autoProvisionRole`. */
  autoProvision?: boolean
  /** Role codename — the roles themselves are created by the install itself,
   *  so there are no ids to pick from yet. */
  autoProvisionRole?: string
}

const productConfigForRepo = (
  repo: ApplicationRepository
): AppProductConfig => ({
  ...createDefaultAppProductConfig({
    name: repo.name,
    description: repo.description ?? '',
    instanceTypeName: repo.name,
    isPublic: Boolean(repo.is_public)
  }),
  productType: 'built_in'
})

export const InstallRepoDrawer = ({
  workspaceId,
  repo,
  open,
  onClose,
  onInstalled
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<FormValues>()

  const { createApp, isCreatingApp } = useWorkspaceAppsList(workspaceId)

  const autoProvision = Form.useWatch('autoProvision', form)
  // Admin roles already reach every workspace admin through the install itself,
  // so handing one out to every member would be a footgun, not a default.
  const roleOptions = (repo?.default_roles ?? [])
    .filter((role) => !role.is_admin)
    .map((role) => ({ value: role.codename, label: role.name }))

  useEffect(() => {
    if (open && repo) {
      form.setFieldsValue({
        name: repo.name,
        description: repo.description ?? '',
        autoProvision: false,
        autoProvisionRole: undefined
      })
    }
  }, [open, repo, form])

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    if (!repo) {
      return
    }
    const cfg = productConfigForRepo(repo)
    const created = await createApp({
      name: values.name,
      description: values.description,
      instance_type_id: repo.id,
      productConfig: cfg,
      autoProvisionRoleCodename: values.autoProvision
        ? values.autoProvisionRole
        : undefined
    })
    saveAppProductConfig(created.id, cfg)
    handleClose()
    if (onInstalled) {
      onInstalled(created.id)
    } else {
      navigate(paths.workspaces.apps.details(String(workspaceId), created.id))
    }
  }

  return (
    <Drawer
      title={t('workspace.apps.builtin.installDrawerTitle')}
      open={open}
      size={480}
      destroyOnHidden
      onClose={handleClose}
      extra={
        <Button
          type="primary"
          loading={isCreatingApp}
          onClick={() => form.submit()}
        >
          {t('workspace.apps.create')}
        </Button>
      }
    >
      {repo ? (
        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
        >
          <Form.Item label={t('workspace.apps.createForm.instanceType')}>
            <Input value={repo.name} disabled />
          </Form.Item>
          {repo.description ? (
            <Flex vertical gap={4} style={{ marginBottom: 14 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {repo.description}
              </Text>
            </Flex>
          ) : null}
          <Form.Item
            label={t('name')}
            name="name"
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Input placeholder={t('placeholders.appName')} />
          </Form.Item>
          <Form.Item
            label={t('workspace.apps.fields.description')}
            name="description"
          >
            <Input.TextArea
              rows={3}
              placeholder={t('placeholders.description')}
            />
          </Form.Item>
          {roleOptions.length ? (
            <>
              <Form.Item
                label={t('workspace.apps.autoProvision.title')}
                name="autoProvision"
                valuePropName="checked"
                extra={t('workspace.apps.autoProvision.description')}
              >
                <Switch />
              </Form.Item>
              {autoProvision ? (
                <Form.Item
                  label={t('workspace.apps.autoProvision.roleLabel')}
                  name="autoProvisionRole"
                  rules={[
                    { required: true, message: t('required_field_error') }
                  ]}
                >
                  <Select
                    options={roleOptions}
                    placeholder={t(
                      'workspace.apps.autoProvision.rolePlaceholder'
                    )}
                  />
                </Form.Item>
              ) : null}
            </>
          ) : null}
        </Form>
      ) : null}
    </Drawer>
  )
}
